export const ROLE_SCOPES = ['organization', 'project', 'department', 'own'] as const
export type RoleScope = (typeof ROLE_SCOPES)[number]
export type RecordStatus = 'active' | 'passive'

export const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'archive', 'delete', 'approve', 'export'] as const
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number]

export type PermissionResource = {
  key: string
  actions: readonly string[]
  sensitive?: readonly string[]
  /** Full permission code for each action token. Absent codes use module.resource.action. */
  codes?: Readonly<Record<string, string>>
  descriptions?: Readonly<Record<string, string>>
}

export type PermissionModule = {
  key: string
  title?: string
  description?: string
  sortOrder?: number
  resources: readonly PermissionResource[]
}

export type PermissionItem = {
  id: string
  code: string
  title: string
  description: string
  module: string
}

export type PermissionGroup = {
  key: string
  title: string
  description: string
  sortOrder: number
  permissions: readonly PermissionItem[]
}

export type RoleAssignment = { userId: string; assignedAt: string; assignedBy: string }

export type RoleHistoryKind = 'created' | 'updated' | 'permissions' | 'users' | 'status'

export type RoleHistoryEntry = {
  id: string
  at: string
  by: string
  kind: RoleHistoryKind
  added?: number
  removed?: number
  status?: RecordStatus
}

export type RoleActivityAction =
  | 'ROLE_CREATE'
  | 'ROLE_UPDATE'
  | 'ROLE_PERMISSIONS_UPDATE'
  | 'ROLE_USERS_UPDATE'
  | 'ROLE_DELETE'
  | string

export type RoleActor = {
  id: string
  email: string
  fullName: string
}

export type RoleActivityEntry = {
  id: string
  action: RoleActivityAction
  actionLabel: string
  actor: RoleActor | null
  actorEmail: string
  actorId: string
  ipAddress: string
  createdAt: string
  summary: string
  payload: Record<string, unknown>
}

export type RoleActivityPage = {
  items: RoleActivityEntry[]
  total: number
  page: number
  limit: number
}

/** Rol liste/detay `permissions[]` satırı (katalog satırıyla aynı şekil). */
export type RolePermission = {
  id: string
  code: string
  description: string
  module: string
}

export type Role = {
  id: string
  name: string
  description: string
  scope: RoleScope
  scopeTargets: string[]
  status: RecordStatus
  isSystem: boolean
  /** Matris / form için permission code listesi */
  permissions: string[]
  /** API’den gelen zengin izin satırları */
  permissionItems: RolePermission[]
  assignments: RoleAssignment[]
  userCount: number
  version: number
  createdAt: string
  updatedAt: string
  createdBy: RoleActor | null
  updatedBy: RoleActor | null
  history: RoleHistoryEntry[]
}

/** Rol formunun alan değerleri (RoleFormModal + RoleInfoFields ortak). */
export type RoleFormValues = {
  name: string
  description: string
  scope: RoleScope
  scopeTargets: string[]
  permissions: string[]
  userIds: string[]
}

export type RoleInput = {
  name: string
  description: string
  scope: RoleScope
  scopeTargets: string[]
  permissions: string[]
  /** `undefined` → update body’den omit; dizi → gönder (boş = tümünü çıkar) */
  userIds?: string[]
  confirmUserReassignment?: boolean
}

export type AffectedRoleUser = {
  id: string
  email: string
  fullName: string
  currentRoleIds: string[]
  currentRoleNames: string[]
}

export type UserRoleReassignmentWarning = {
  operation: string
  message: string
  affectedCount: number
  affectedUsers: AffectedRoleUser[]
}

export type AccessUser = {
  id: string
  name: string
  email: string
  departmentId: string
  department: string
  position: string
  avatarUrl?: string
}

/** Personel listesi — kullanıcı bağlama seçici */
export type LinkableEmployee = AccessUser & {
  workEmail: string
  isActive: boolean
  hasUser: boolean
  userId: string | null
}

export type MailProviderOption = {
  id: string
  name: string
  kind: string
  isDefault?: boolean
}

export type MailboxInput = {
  providerId: string
  secret: string
  signatureHtml?: string
  email?: string
}

/** GET /users/{id}.mailbox — secret asla dönmez */
export type UserMailbox = {
  id: string
  providerId: string
  providerName: string
  providerKind: string
  email: string
  signatureHtml: string
  hasSecret: boolean
  isActive: boolean
}

/** PUT /users/{id} mailbox patch */
export type MailboxUpdateInput =
  | { remove: true }
  | {
      remove?: false
      providerId?: string
      email?: string
      secret?: string
      signatureHtml?: string
    }

export type AccessProject = { id: string; name: string }

export type AssignedPerson = {
  id: string
  firstName: string
  lastName: string
  name: string
  email: string
  position?: string
  avatarUrl?: string
}

export type DepartmentRef = { id: string; name: string; code: string }

export type DepartmentManager = {
  id: string
  firstName: string
  lastName: string
  email: string
  name: string
  avatarUrl?: string
}

export type Department = {
  id: string
  name: string
  code: string
  description: string
  managerId: string | null
  parentId: string | null
  parent: DepartmentRef | null
  isActive: boolean
  manager: DepartmentManager | null
  members: AssignedPerson[]
  memberCount: number
  status: RecordStatus
  createdAt: string
}

export type DepartmentInput = {
  name: string
  description: string
  managerId: string
  parentId: string | null
  /** `undefined` → body’den çıkar (update’te atama değişmedi). Dolu/boş dizi → gönderilir. */
  memberIds?: string[]
  isActive: boolean
  confirmEmployeeReassignment?: boolean
}

export type EmployeeReassignmentReason = 'OTHER_DEPARTMENT' | 'POSITION_OTHER_DEPARTMENT' | string

export type Employee = {
  id: string
  employee_number: string
  first_name: string
  last_name: string
  birth_date: string
  identity_number: string | null
  nationality_code: string
  gender: string
  marital_status: string
  department_id: string
  department: Department | null
  position_id: string | null
  position: Position | null
  manager_employee_id: string
  manager: Employee | null
  employment_type: string
  work_model: string
  job_title: string | null
  hire_date: string
  termination_date: string | null
  is_active: boolean
  phone: string
  work_phone: string | null
  personal_email: string
  work_email: string | null
  masked_iban: string | null
  bank_name: string | null
  photo_file_id: string | null
  photo_url: string | null
  user_id: string | null
  user_email: string | null
  created_at: string
  updated_at: string
}

export type AffectedEmployee = {
  id: string
  firstName: string
  lastName: string
  name: string
  email: string
  currentDepartmentId: string
  currentDepartmentName: string
  currentPositionId: string
  currentPositionTitle: string
  currentPositionDepartmentId: string
  reasons: EmployeeReassignmentReason[]
  willClearPosition: boolean
}

export type EmployeeReassignmentWarning = {
  operation: string
  message: string
  affectedCount: number
  affectedEmployees: AffectedEmployee[]
}


export type DepartmentPage = { items: Department[]; total: number }

export const POSITION_LEVELS = ['JUNIOR', 'MID', 'SENIOR', 'LEAD', 'MANAGER', 'DIRECTOR'] as const
export type PositionLevel = (typeof POSITION_LEVELS)[number]

export type PositionDepartment = DepartmentRef & {
  manager: DepartmentManager | null
  parent: DepartmentRef | null
}

export type Position = {
  id: string
  title: string
  code: string
  description: string
  level: PositionLevel
  isActive: boolean
  departmentId: string
  department: PositionDepartment | null
  employees: AssignedPerson[]
  createdAt: string
}

export type PositionInput = {
  title: string
  description: string
  level: PositionLevel
  departmentId: string
  confirmDepartmentChange?: boolean
}

export type PositionPage = { items: Position[]; total: number }

export type DirectoryUserRow = {
  id: string
  name: string
  firstName: string
  lastName: string
  email: string
  isActive: boolean
  roleIds: string[]
  roleNames: string[]
  employeeId: string | null
  avatarUrl?: string
  departmentName: string
  position: string
  mailbox: UserMailbox | null
}

export type DirectoryUserPage = { items: DirectoryUserRow[]; total: number }

export type DirectoryUserInput = {
  employeeId: string
  roleIds: string[]
  /** Opsiyonel; verilirse personel work_email ile aynı olmalı */
  email?: string
  firstName?: string
  lastName?: string
  /** Sistem giriş şifresi. Boş → geçici şifre work_email’e gider */
  password?: string
  /** SMTP/IMAP App Password — giriş şifresiyle karıştırılmamalı */
  mailbox?: MailboxInput
}

export type MailAccountInput = {
  userId: string
  providerId: string
  email: string
  secret: string
  signatureHtml?: string
}
